import { Component, OnDestroy } from '@angular/core';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { SessionItemData } from '@workflow/services/session-item-data';
import { JSONSchema7 } from 'json-schema';
import { SplitButtonElement } from '@app/components/elements/splitbutton/splitbutton.component';
import { ProjectService } from '@app/services/project/project.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Resolver } from '@stoplight/json-ref-resolver';
import { ProjectDto } from '@api/projects/interfaces';
import { projectPages } from '@app/pages/project/projects.routes';
import { distinctUntilChanged, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { MenuService } from '@app/services/menu';
import { TranslateService } from '@ngx-translate/core';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { ErrorNotificationService } from '@app/services/error-notification/error-notification.service';
import { PermissionService } from '@app/services/permission/permission.service';
import { cloneDeep } from 'lodash';
import * as schema from './project-schema.json';

@Component({
	selector: 'app-project-edit',
	templateUrl: './project-edit.component.html',
	styleUrls:['./project-edit.component.scss']
})
export class ProjectEditComponent implements OnDestroy {
	private subscription = new Subscription();

	projectFormID = 'form-edit-project';
	data: SessionItemData = null;
	resolvedSchema: JSONSchema7 = null;
	project: ProjectDto;

	public readonly isReadonly$: Observable<boolean>;

	public projectEditSplitButton: SplitButtonElement = {
		title: 'projects.doEdit',
		icon: 'save',
		type: 'primary',
		buttonType: 'submit',
		attrForm: this.projectFormID,
	};
	public projectCancelSplitButton: SplitButtonElement = {
		title: 'projects.cancel',
		icon: 'x',
		type: 'default',
		callback: () => this.showProjectList(),
	};
	public projectArchiveUploadSplitButton: SplitButtonElement = {
		title: 'projects.uploadArchive',
		icon: 'upload',
		type: 'primary',
	};

	fileList: NzUploadFile[] = [];
	zipRequest = async (file: NzUploadFile): Promise<void> => {
		try {
			await this.projectService.uploadProjectArchive$(this.project.project_id, file.file).toPromise();
			this.notification.success('', 'Projekt-Archiv erfolgreich hochgeladen');
			this.projectService.refreshProjects();
			this.showProjectList();
		} catch (error) {
			this.errorService.showErrorNotification(error, 'projects.error.archiveUploadFailed');
			this.fileList = [];
		}
	};

	constructor(
		private projectService: ProjectService,
		private notification: NzNotificationService,
		private errorService: ErrorNotificationService,
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private menuService: MenuService,
		private translateService: TranslateService,
		permissionService:PermissionService
	) {
		this.isReadonly$ = permissionService.hasPermission$('projects:write').pipe(map(hasPermission=>!hasPermission));

		const project$ = this.activatedRoute.params.pipe(
			map(params => params['project_id']),
			distinctUntilChanged(),
			switchMap(projectId => this.projectService.getProjectById(projectId)),
			filter(projectDto => !!projectDto),
		);
		
		const title$ = project$.pipe(
			switchMap((project) => this.translateService.stream('actions.edit.item', {item: project.projectname})),
		);
		
		const hasProjectConfigPermission$ = project$.pipe(
			switchMap(project => {
				const permissions$ = permissionService.getPermissionsByProjectId(project.project_id)
				return permissionService.hasPermissionInObs$(permissions$, 'project-config:read');
			}),
		);
		
		const schema$ = combineLatest([project$, hasProjectConfigPermission$]).pipe(
			switchMap(([project, hasPermission]) => hasPermission ? projectService.getProjectConfigById(project.project_id) : of({ schema: undefined })),
			withLatestFrom(new Resolver().resolve(schema)),
			map(([projectConfig, resolvedSchema]) => {
				if (!projectConfig?.schema) {
					return resolvedSchema.result;
				}
				const schema = cloneDeep(resolvedSchema.result as JSONSchema7 & {$order?: string[]});
				schema.properties.metaData = projectConfig.schema;
				schema.$order ??= ['projectInfo'];
				schema.$order.push('metaData');
				return schema;
			})
		);
		
		this.subscription.add(project$.subscribe(projectDto => {
			this.project = projectDto;
			this.data = new SessionItemData({ projectInfo: projectDto, metaData: projectDto?.data});
		}));
		this.subscription.add(title$.subscribe(title => this.menuService.title = title));
		this.subscription.add(schema$.subscribe(schema => this.resolvedSchema = schema));
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	async editProject(isFormValid: boolean): Promise<void> {
		if (!isFormValid) {
			return;
		}
		const projectDto: ProjectDto = {
			...this.data.data.projectInfo as ProjectDto,
			data: this.data.data.metaData,
		};
		await this.projectService.updateProject$(
			projectDto
		).toPromise().then(project => {
			this.notification.success('', 'Projekt ' + project.projectname + ' erfolgreich bearbeitet');
		}).catch((error) => {
			this.errorService.showErrorNotification( error, 'projects.error.editFailed');
		});
		this.projectService.refreshProjects();
		this.showProjectList();
	}

	showProjectList(): void {
		this.router
			.navigate([projectPages.dashboard.base()])
			.catch(console.error);
	}

	setSubmitActive(active: boolean): void {
		this.projectEditSplitButton.disabled = !active;
	}

	uploadButtonTouchEnd(event: TouchEvent): void {
		event.stopPropagation();
	}
}
